import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ArrowDownLeft, ArrowUpRight, Ticket, Trophy,
  RotateCcw, ExternalLink, Share2
} from 'lucide-react';
import { buildTweetUrl } from '../components/WinCelebrationModal';

const tg = () => window.Telegram?.WebApp;

const shareWinOnX = (tx) => {
  const shareUrl = `${API}/share/win/${tx.win_id}`;
  const text = `I just won ${tx.amount?.toFixed(2)} XRP on Vitamin C Gainz! 🍊⚡ Pick a number 0-9 — the Illinois Pick 3 Fireball decides the winner.`;
  const url = buildTweetUrl(text, shareUrl);
  const telegram = tg();
  if (telegram?.openLink) telegram.openLink(url);
  else window.open(url, '_blank', 'noopener');
};

function Transactions() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchTransactions();

    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }
    return () => {
      if (telegram) {
        telegram.BackButton.hide();
        telegram.BackButton.offClick(() => navigate('/'));
      }
    };
  }, [isAuthenticated, authLoading]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/user/transactions?limit=50`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTxIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-emerald-400" />;
      case 'withdrawal': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'slot_purchase': return <Ticket className="w-4 h-4 text-indigo-400" />;
      case 'winnings': return <Trophy className="w-4 h-4 text-yellow-400" />;
      case 'refund': return <RotateCcw className="w-4 h-4 text-amber-400" />;
      default: return <ArrowDownLeft className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTxColor = (type) => {
    switch (type) {
      case 'deposit': case 'winnings': case 'refund': return 'text-emerald-400';
      case 'withdrawal': case 'slot_purchase': return 'text-red-400';
      default: return 'text-slate-300';
    }
  };

  const getTxSign = (type) => {
    switch (type) {
      case 'deposit': case 'winnings': case 'refund': return '+';
      case 'withdrawal': case 'slot_purchase': return '-';
      default: return '';
    }
  };

  const getTxLabel = (type) => {
    switch (type) {
      case 'deposit': return 'Deposit';
      case 'withdrawal': return 'Withdrawal';
      case 'slot_purchase': return 'Slot Purchase';
      case 'winnings': return 'Winnings';
      case 'refund': return 'Refund';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-bold text-white leading-tight">Transactions</h1>
              <p className="text-[11px] text-slate-500">{transactions.length} transactions</p>
            </div>
          </div>
          <Button data-testid="refresh-btn" onClick={fetchTransactions} size="sm" variant="ghost" className="text-slate-400 h-8 px-2">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Transaction List */}
      <div className="px-4 pt-4">
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No transactions yet</p>
            <p className="text-[11px] text-slate-600 mt-1">Deposits, purchases, and winnings will appear here</p>
          </div>
        ) : (
          <div className="space-y-1">
            {transactions.map((tx, idx) => (
              <div
                key={tx.tx_id || idx}
                data-testid={`tx-${idx}`}
                className="flex items-center gap-3 py-3 border-b border-slate-800/50 last:border-0"
              >
                <div className="w-9 h-9 rounded-xl bg-slate-800/60 flex items-center justify-center flex-shrink-0">
                  {getTxIcon(tx.tx_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-white">{getTxLabel(tx.tx_type)}</p>
                    <p className={`text-sm font-bold ${getTxColor(tx.tx_type)}`}>
                      {getTxSign(tx.tx_type)}{tx.amount?.toFixed(2)} {tx.currency === 'FUZZY' ? 'FUZZY' : 'XRP'}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-[10px] text-slate-500">{formatDate(tx.created_at)}</p>
                    <div className="flex items-center gap-2">
                      {tx.tx_type === 'winnings' && tx.win_id && (
                        <button
                          data-testid={`tx-share-win-${idx}`}
                          onClick={() => shareWinOnX(tx)}
                          className="flex items-center gap-0.5 text-[10px] text-orange-400 hover:text-orange-300 font-semibold"
                        >
                          <Share2 className="w-2.5 h-2.5" />
                          Share win
                        </button>
                      )}
                      {tx.xrp_tx_hash && (
                        <a
                          href={`https://testnet.xrpl.org/transactions/${tx.xrp_tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-0.5 text-[10px] text-indigo-400 hover:text-indigo-300"
                          data-testid={`tx-link-${idx}`}
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          {tx.xrp_tx_hash.slice(0, 8)}...
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Transactions;
